/**
 * Iter 58 — Labour Master (HR / Site Engineer view).
 * Search, filter (incl. mobile), Excel import, inline EDIT, face-enrolment +
 * on-site status. Works on desktop + mobile (filters wrap; table scrolls).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Upload, Download, ScanFace, X, Camera, Pencil, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { apiGet, apiDel, apiPut, apiPost, API } from '../lib/api';
import axios from 'axios';

const EMPTY_FORM = {
  name: '', aadhar_number: '', mobile: '', dob: '',
  trade: '', designation: '', gender: '', daily_wage: '', hourly_wage: '',
  supervisor_name: '', posting_type: '', subcontractor_id: '',
  pan_number: '', bank_account_no: '', bank_ifsc: '', bank_name: '',
  address: '', esi_number: '', pf_number: '',
};

export default function LabourMaster() {
  const navigate = useNavigate();
  const [labour, setLabour] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [filters, setFilters] = useState({ trade: '', gender: '', posting_type: '', subcontractor_id: '', face_enrolled: '', mobile: '', q: '' });
  const [masters, setMasters] = useState({ labour_trades: [], labour_genders: [] });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [hoursMap, setHoursMap] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      if (showInactive) params.set('show_inactive', 'true');
      const r = await apiGet(`/labour-master?${params.toString()}`);
      setLabour(r.items || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  const loadHours = async () => {
    try {
      const r = await apiGet('/labour-master/hours-worked');
      const map = {};
      (r.rows || []).forEach((row) => {
        map[row.labour_id] = {
          hours: row.total_hours,
          shifts: row.total_shifts,   // Change 5
          in_progress: row.in_progress,
        };
      });
      setHoursMap(map);
    } catch { /* silent */ }
  };
  const loadSubcontractors = async () => {
    try {
      const r = await apiGet('/subcontractors');
      setSubcontractors(Array.isArray(r) ? r : (r.items || []));
    } catch { /* silent */ }
  };
  useEffect(() => {
    apiGet('/config-masters').then((r) => setMasters(r.masters || {})).catch(() => {});
    loadSubcontractors();
    load(); // eslint-disable-next-line
    loadHours(); // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); loadHours(); /* eslint-disable-next-line */ }, [filters.trade, filters.gender, filters.subcontractor_id, filters.posting_type, filters.face_enrolled, filters.mobile, showInactive]);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/labour-master/template.xlsx`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'labour-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };

  const uploadExcel = async (file) => {
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/labour-master/import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.created} · skipped ${r.data.skipped}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this labour record? Their attendance history will be retained.')) return;
    try { await apiDel(`/labour-master/${id}`); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  // ─── FIX 1 — inline edit ───
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      name: l.name || '', aadhar_number: l.aadhar_number || '', mobile: l.mobile || '',
      dob: (l.dob || '').slice(0, 10),
      trade: l.trade || '', designation: l.designation || '', gender: l.gender || '',
      daily_wage: l.daily_wage ?? '', hourly_wage: l.hourly_wage ?? '',
      supervisor_name: l.supervisor_name || '', posting_type: l.posting_type || '',
      subcontractor_id: l.subcontractor_id || '',
      pan_number: l.pan_number || '', bank_account_no: l.bank_account_no || '',
      bank_ifsc: l.bank_ifsc || '', bank_name: l.bank_name || '',
      address: l.address || '', esi_number: l.esi_number || '', pf_number: l.pf_number || '',
    });
  };
  const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        daily_wage: form.daily_wage === '' ? null : Number(form.daily_wage),
        hourly_wage: form.hourly_wage === '' ? null : Number(form.hourly_wage),
        subcontractor_id: form.subcontractor_id || null,
      };
      const res = await apiPut(`/labour-master/${editing.id}`, payload);
      const updated = res?.labour;
      // In-place row refresh (optimistic) — fall back to a full reload.
      if (updated) {
        setLabour((rows) => rows.map((r) => (r.id === editing.id ? { ...r, ...updated } : r)));
      } else {
        load();
      }
      toast.success('Saved');
      closeEdit();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || 'Save failed');
    } finally { setSaving(false); }
  };

  // ─── FIX 3 — on-site badge ───
  const OnSiteBadge = ({ hr, id }) => {
    if (!hr) return null;
    if (hr.in_progress) {
      return (
        <span data-testid={`labour-onsite-${id}`}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> On-site
        </span>
      );
    }
    return (
      <span data-testid={`labour-wasonsite-${id}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">
        ✓ Was on-site
      </span>
    );
  };

  const inp = 'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#1F7A6E] focus:outline-none';
  const lbl = 'block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1';

  return (
    <div>
      <PageHeader
        overline="HR · Field"
        title="Labour Master"
        subtitle="Workforce database with Aadhar, bank, and face-enrolment status. Import bulk lists from Excel."
      />

      {/* Filters + actions */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-end gap-2" data-testid="labour-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Search name / aadhar / mobile…"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1F7A6E] focus:outline-none"
            data-testid="labour-search"
          />
        </div>
        <input
          value={filters.mobile}
          onChange={(e) => setFilters({ ...filters, mobile: e.target.value })}
          placeholder="Mobile…"
          inputMode="tel"
          className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white w-32"
          data-testid="labour-mobile-filter"
        />
        <select value={filters.subcontractor_id} onChange={(e) => setFilters({ ...filters, subcontractor_id: e.target.value })} className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white" data-testid="labour-subcontractor-filter">
          <option value="">All Subcontractors</option>
          {subcontractors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filters.trade} onChange={(e) => setFilters({ ...filters, trade: e.target.value })} className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white" data-testid="labour-trade-filter">
          <option value="">All Trades</option>
          {(masters.labour_trades || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.posting_type} onChange={(e) => setFilters({ ...filters, posting_type: e.target.value })} className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white" data-testid="labour-posting-filter">
          <option value="">All Types</option>
          <option value="Site">Site</option>
          <option value="Office">Office</option>
        </select>
        <select value={filters.gender} onChange={(e) => setFilters({ ...filters, gender: e.target.value })} className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white" data-testid="labour-gender-filter">
          <option value="">All Genders</option>
          {(masters.labour_genders || []).map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filters.face_enrolled} onChange={(e) => setFilters({ ...filters, face_enrolled: e.target.value })} className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white" data-testid="labour-face-filter">
          <option value="">Face: All</option>
          <option value="true">Enrolled</option>
          <option value="false">Not Enrolled</option>
        </select>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={downloadTemplate} className="flex items-center gap-1 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 rounded-sm hover:border-[#1F7A6E]" data-testid="labour-template-btn">
            <Download size={13} /> Template
          </button>
          <label className="flex items-center gap-1 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider bg-[#1F7A6E] text-white rounded-sm hover:bg-[#2f3a5d] cursor-pointer" data-testid="labour-import-btn">
            <Upload size={13} /> {importing ? 'Importing…' : 'Import Excel'}
            <input type="file" accept=".xlsx" className="hidden" disabled={importing} onChange={(e) => { if (e.target.files[0]) uploadExcel(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <button onClick={() => navigate('/m/labour/enroll')} className="flex items-center gap-1 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm hover:bg-emerald-700" data-testid="labour-enroll-cta">
            <ScanFace size={13} /> Enrol Face
          </button>
          <button
            onClick={() => setShowInactive(s => !s)}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${
              showInactive ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="labour-show-inactive-toggle"
          >
            {showInactive ? 'Showing Inactive' : 'Active Only'}
          </button>
        </div>
      </div>

      {/* Import result panel */}
      {importResult && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-3 text-sm" data-testid="labour-import-result">
          <div className="flex items-start justify-between gap-3">
            <div>
              <b className="text-emerald-800">{importResult.created} labour records imported</b>
              {importResult.skipped > 0 && <span className="ml-2 text-amber-700">· {importResult.skipped} skipped</span>}
              {(importResult.errors || []).length > 0 && (
                <ul className="mt-2 text-xs text-rose-700 list-disc pl-5 max-h-32 overflow-y-auto">
                  {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
            <button onClick={() => setImportResult(null)} className="text-slate-500 hover:text-slate-900"><X size={14} /></button>
          </div>
        </div>
      )}

      {/* ─── Mobile card view (phones) ─── */}
      <div className="sm:hidden space-y-3" data-testid="labour-cards">
        {loading && <div className="text-center py-8 text-sm text-slate-400">Loading…</div>}
        {!loading && labour.length === 0 && (
          <div className="text-center py-12 text-sm text-slate-500 bg-white border border-slate-200 rounded-sm">
            No labour records yet. Use <b>Import Excel</b> to upload your workforce list.
          </div>
        )}
        {labour.map((l) => {
          const hr = hoursMap[l.id];
          return (
            <div key={l.id} className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`labour-card-${l.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.mobile || '—'} · {l.trade || '—'}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(l)} className="text-slate-500 hover:text-[#1F7A6E]" data-testid={`labour-card-edit-${l.id}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(l.id)} className="text-rose-600 hover:text-rose-800"><X size={15} /></button>
                </div>
              </div>
              {/* On-site badge — most prominent thing after the name */}
              {hr && (
                <div className="mt-2">
                  {hr.in_progress
                    ? <span data-testid={`labour-card-onsite-${l.id}`} className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> On-site now</span>
                    : <span data-testid={`labour-card-wasonsite-${l.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full bg-slate-100 text-slate-500">✓ Was on-site ({hr.hours}h)</span>}
                </div>
              )}
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                <div><span className="text-slate-400">Wage:</span> {l.daily_wage ? `₹${l.daily_wage}` : '—'}</div>
                <div className="truncate"><span className="text-slate-400">Subcontractor:</span> {l.subcontractor_name || '—'}</div>
                <div><span className="text-slate-400">Hours today:</span> {hr ? `${hr.hours}h` : '—'}</div>
              </div>
              <div className="mt-2">
                {l.face_enrolled
                  ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Face Enrolled</span>
                  : <a href={`/m/labour/enroll?labour_id=${l.id}&name=${encodeURIComponent(l.name)}&from=list`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1F7A6E] text-white font-bold inline-flex items-center gap-1"><Camera size={10} /> Enrol Face</a>}
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── Desktop table ─── */}
      <div className="hidden sm:block bg-white border border-slate-200 rounded-sm overflow-x-auto overflow-y-auto max-h-[70vh]">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th>Name</th><th>Aadhar</th><th>Mobile</th><th>Trade</th>
              <th>Type</th><th>Wage/day</th><th>Subcontractor</th><th>Supervisor</th><th>Hours</th><th>Shifts</th><th>Face</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="text-center py-8 text-sm text-slate-400">Loading…</td></tr>}
            {!loading && labour.length === 0 && (
              <tr><td colSpan={11} className="text-center py-12 text-sm text-slate-500">
                No labour records yet. Use <b>Import Excel</b> to upload your workforce list.
              </td></tr>
            )}
            {labour.map((l) => {
              const hr = hoursMap[l.id];
              return (
                <tr key={l.id} data-testid={`labour-row-${l.id}`} className={l.is_active === false ? 'bg-slate-50 opacity-70' : ''}>
                  <td className="font-display font-semibold">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{l.name}</span>
                      <OnSiteBadge hr={hr} id={l.id} />
                      {l.is_active === false && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold" data-testid={`labour-inactive-badge-${l.id}`}>
                          Inactive
                        </span>
                      )}
                    </div>
                    {l.is_active === false && l.deactivated_at && (
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {l.deactivated_reason === 'auto_7day_absence' ? 'Auto-deactivated' : 'Deactivated'} · {l.last_punch_date || 'never punched'}
                      </div>
                    )}
                  </td>
                  <td className="text-xs font-mono">{l.aadhar_number}</td>
                  <td className="text-xs">{l.mobile || '—'}</td>
                  <td className="text-xs">{l.trade || '—'}</td>
                  <td className="text-xs">{l.posting_type || l.designation || '—'}</td>
                  <td className="text-xs">{l.daily_wage ? `₹${l.daily_wage}` : '—'}</td>
                  <td className="text-xs truncate max-w-[140px]" data-testid={`labour-subcontractor-${l.id}`}>{l.subcontractor_name || '—'}</td>
                  <td className="text-xs truncate max-w-[140px]" data-testid={`labour-supervisor-${l.id}`}>{l.supervisor_name || '—'}</td>
                  <td className="text-xs tabular-nums" data-testid={`labour-hours-${l.id}`}>
                    {hr ? <span className="text-slate-800 font-semibold" data-testid={`labour-hours-${l.id}`}>{hr.hours}h</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="text-center">
                    {hr?.shifts != null ? <span className="text-[#1F7A6E] font-bold tabular-nums" data-testid={`labour-shifts-${l.id}`}>{hr.shifts.toFixed(2)}</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td>
                    {l.face_enrolled ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Enrolled</span>
                        <a href={`/m/labour/enroll?labour_id=${l.id}&name=${encodeURIComponent(l.name)}&from=list&force=1`}
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-slate-300 text-slate-600 font-bold hover:border-[#1F7A6E] inline-flex items-center gap-1"
                          data-testid={`labour-reenrol-${l.id}`}>
                          <Camera size={9} /> Re-enrol
                        </a>
                      </div>
                    ) : (
                      <a href={`/m/labour/enroll?labour_id=${l.id}&name=${encodeURIComponent(l.name)}&from=list`}
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1F7A6E] text-white font-bold hover:bg-[#14504E] inline-flex items-center gap-1"
                        data-testid={`labour-enrol-${l.id}`}>
                        <Camera size={10} /> Enrol Face
                      </a>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openEdit(l)} className="text-slate-500 hover:text-[#1F7A6E]" title="Edit" data-testid={`labour-edit-${l.id}`}><Pencil size={14} /></button>
                      {l.is_active === false ? (
                        <button
                          onClick={async () => { try { await apiPost(`/labour-master/${l.id}/reactivate`, {}); toast.success('Reactivated'); load(); } catch { toast.error('Failed'); } }}
                          className="text-xs text-emerald-600 hover:text-emerald-800 border border-emerald-300 rounded px-1.5 py-0.5 font-bold"
                          title="Reactivate" data-testid={`labour-reactivate-${l.id}`}
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={async () => { if (!window.confirm(`Deactivate ${l.name}?`)) return; try { await apiPost(`/labour-master/${l.id}/deactivate`, { reason: 'manual' }); toast.success('Deactivated'); load(); } catch { toast.error('Failed'); } }}
                          className="text-amber-600 hover:text-amber-800" title="Deactivate"
                          data-testid={`labour-deactivate-${l.id}`}
                        >
                          <X size={13} />
                        </button>
                      )}
                      <button onClick={() => remove(l.id)} className="text-rose-600 hover:text-rose-800" title="Remove"><X size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ─── Edit drawer ─── */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={closeEdit}>
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="labour-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400">Edit Labour</div>
                <h3 className="font-display font-bold text-lg">{editing.name}</h3>
              </div>
              <button onClick={closeEdit} className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className={lbl}>Name *</label><input className={inp} value={form.name} onChange={(e) => setF('name', e.target.value)} data-testid="edit-name" /></div>
              <div><label className={lbl}>Mobile *</label><input className={inp} value={form.mobile} onChange={(e) => setF('mobile', e.target.value)} inputMode="tel" data-testid="edit-mobile" /></div>
              <div><label className={lbl}>Aadhar *</label><input className={inp} value={form.aadhar_number} onChange={(e) => setF('aadhar_number', e.target.value)} data-testid="edit-aadhar" /></div>
              <div><label className={lbl}>Date of Birth *</label><input type="date" className={inp} value={form.dob} onChange={(e) => setF('dob', e.target.value)} data-testid="edit-dob" /></div>
              <div><label className={lbl}>Gender</label>
                <select className={inp} value={form.gender} onChange={(e) => setF('gender', e.target.value)} data-testid="edit-gender">
                  <option value="">—</option>
                  {(masters.labour_genders || ['Male', 'Female', 'Other']).map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              {/* Subcontractor — the primary reason for this edit feature */}
              <div className="col-span-2"><label className={lbl}>Subcontractor</label>
                <select className={inp} value={form.subcontractor_id} onChange={(e) => setF('subcontractor_id', e.target.value)} data-testid="labour-edit-subcontractor">
                  <option value="">— No subcontractor —</option>
                  {subcontractors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {subcontractors.length === 0 && (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="edit-no-subcontractors">No approved subcontractors yet — add via Labour → Subcontractors.</p>
                )}
                {!form.subcontractor_id && editing.subcontractor_name && (
                  <p className="text-[11px] text-slate-500 mt-1">Currently linked free-text: <b>{editing.subcontractor_name}</b> (not linked to master — pick one above to formalise).</p>
                )}
              </div>

              <div><label className={lbl}>Trade</label>
                <select className={inp} value={form.trade} onChange={(e) => setF('trade', e.target.value)} data-testid="edit-trade">
                  <option value="">—</option>
                  {(masters.labour_trades || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label className={lbl}>Designation</label><input className={inp} value={form.designation} onChange={(e) => setF('designation', e.target.value)} data-testid="edit-designation" /></div>
              <div><label className={lbl}>Daily Wage (₹)</label><input type="number" className={inp} value={form.daily_wage} onChange={(e) => setF('daily_wage', e.target.value)} data-testid="edit-daily-wage" /></div>
              <div><label className={lbl}>Hourly Wage (₹)</label><input type="number" className={inp} value={form.hourly_wage} onChange={(e) => setF('hourly_wage', e.target.value)} data-testid="edit-hourly-wage" /></div>
              <div><label className={lbl}>Supervisor</label><input className={inp} value={form.supervisor_name} onChange={(e) => setF('supervisor_name', e.target.value)} data-testid="edit-supervisor" /></div>
              <div><label className={lbl}>Posting Type</label>
                <select className={inp} value={form.posting_type} onChange={(e) => setF('posting_type', e.target.value)} data-testid="edit-posting-type">
                  <option value="">—</option>
                  <option value="Office">Office</option>
                  <option value="Site">Site</option>
                </select>
              </div>

              <div><label className={lbl}>PAN</label><input className={inp} value={form.pan_number} onChange={(e) => setF('pan_number', e.target.value)} /></div>
              <div><label className={lbl}>Bank A/C No.</label><input className={inp} value={form.bank_account_no} onChange={(e) => setF('bank_account_no', e.target.value)} /></div>
              <div><label className={lbl}>Bank IFSC</label><input className={inp} value={form.bank_ifsc} onChange={(e) => setF('bank_ifsc', e.target.value)} /></div>
              <div><label className={lbl}>Bank Name</label><input className={inp} value={form.bank_name} onChange={(e) => setF('bank_name', e.target.value)} /></div>
              <div><label className={lbl}>ESI No.</label><input className={inp} value={form.esi_number} onChange={(e) => setF('esi_number', e.target.value)} /></div>
              <div><label className={lbl}>PF No.</label><input className={inp} value={form.pf_number} onChange={(e) => setF('pf_number', e.target.value)} /></div>
              <div className="col-span-2"><label className={lbl}>Address</label><textarea className={inp} rows={2} value={form.address} onChange={(e) => setF('address', e.target.value)} /></div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={closeEdit} className="px-4 py-2 text-sm border border-slate-300 rounded-sm font-display font-semibold">Cancel</button>
              <button onClick={saveEdit} disabled={saving} data-testid="labour-edit-save"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#14504E] disabled:opacity-50">
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
